/**
 * Daily Usage Manager
 * Quản lý logic kiểm tra, trừ lượt, reset theo ngày cho hệ thống free usage model
 */

import {
  getDatabase,
  ref,
  get,
  set,
  update,
  runTransaction,
} from "firebase/database";
import moment from "moment-timezone";
import { USAGE_CONFIG } from "../config/usageConfig";

// Timezone cố định cho Việt Nam
const VN_TIMEZONE = USAGE_CONFIG.VN_TIMEZONE;

// Default daily limit - lấy từ config (từ env)
const DEFAULT_DAILY_LIMIT = USAGE_CONFIG.DEFAULT_DAILY_LIMIT;

/**
 * Generate unique request ID
 * @param {string} prefix
 * @returns {string}
 */
export const generateRequestId = (prefix = "req") => {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Lấy ngày hiện tại theo timezone VN (format: YYYY-MM-DD)
 */
export const getCurrentDateVN = () => {
  return moment().tz(VN_TIMEZONE).format("YYYY-MM-DD");
};

/**
 * Lấy timestamp của 00:00 ngày hôm nay (VN timezone)
 */
export const getTodayStartTimestamp = () => {
  return moment().tz(VN_TIMEZONE).startOf("day").valueOf();
};

/**
 * Lấy timestamp của 00:00 ngày mai (VN timezone)
 */
export const getTomorrowStartTimestamp = () => {
  return moment().tz(VN_TIMEZONE).add(1, "day").startOf("day").valueOf();
};

/**
 * Tính thời gian còn lại đến khi reset (00:00 ngày mai)
 * @returns {Object} { hours, minutes, seconds, totalSeconds }
 */
export const getTimeUntilReset = () => {
  const now = moment().tz(VN_TIMEZONE);
  const tomorrow = moment().tz(VN_TIMEZONE).add(1, "day").startOf("day");
  const duration = moment.duration(tomorrow.diff(now));

  return {
    hours: Math.floor(duration.asHours()),
    minutes: duration.minutes(),
    seconds: duration.seconds(),
    totalSeconds: Math.floor(duration.asSeconds()),
    formattedTime: `${Math.floor(
      duration.asHours()
    )} giờ ${duration.minutes()} phút`,
  };
};

/**
 * Lấy daily limit cho user
 * Ưu tiên: Custom limit > Default limit
 * @param {string} userId
 * @returns {Promise<number>}
 */
export const getDailyLimit = async (userId) => {
  try {
    const db = getDatabase();

    // 1. Check custom limit cho user cụ thể
    const customLimitRef = ref(db, `/custom-limits/${userId}`);
    const customLimitSnapshot = await get(customLimitRef);

    if (customLimitSnapshot.exists()) {
      const customLimitData = customLimitSnapshot.val();

      // Check expiration nếu có
      if (customLimitData.expiresAt && customLimitData.expiresAt < Date.now()) {
        // Custom limit đã hết hạn, xóa đi
        await set(customLimitRef, null);
      } else {
        const customLimit = customLimitData.dailyLimit || DEFAULT_DAILY_LIMIT;
        console.log(`✅ getDailyLimit: Using custom limit for user ${userId}: ${customLimit}`);
        return customLimit;
      }
    }

    // 2. Nếu không có custom limit, lấy default limit từ system settings
    const defaultLimitRef = ref(
      db,
      `/system-settings/dailyLimits/defaultLimit`
    );
    const defaultLimitSnapshot = await get(defaultLimitRef);

    if (defaultLimitSnapshot.exists()) {
      const dbLimit = defaultLimitSnapshot.val();
      console.log(`✅ getDailyLimit: Using Firebase default limit: ${dbLimit}`);
      return dbLimit;
    }

    // 3. Fallback về constant nếu không có gì trong DB
    console.log(`✅ getDailyLimit: Using config default limit (env): ${DEFAULT_DAILY_LIMIT}`);
    return DEFAULT_DAILY_LIMIT;
  } catch (error) {
    console.error("❌ Error getting daily limit:", error);
    console.log(`✅ getDailyLimit: Error, falling back to config default: ${DEFAULT_DAILY_LIMIT}`);
    return DEFAULT_DAILY_LIMIT; // Fallback
  }
};

/**
 * Reset daily usage nếu cần (khi qua ngày mới)
 * @param {string} userId
 * @returns {Promise<boolean>} true nếu đã reset
 */
export const resetDailyUsageIfNeeded = async (userId) => {
  try {
    const db = getDatabase();
    const dailyUsageRef = ref(db, `/users/${userId}/dailyUsage`);
    const dailyUsageSnapshot = await get(dailyUsageRef);

    const currentDate = getCurrentDateVN();

    if (dailyUsageSnapshot.exists()) {
      const dailyUsageData = dailyUsageSnapshot.val();

      // Nếu currentDate trong DB khác ngày hiện tại → Reset
      if (dailyUsageData.currentDate !== currentDate) {
        console.log(
          `🔄 Resetting daily usage for user ${userId} (new day: ${currentDate})`
        );

        await update(dailyUsageRef, {
          currentDate: currentDate,
          usedCount: 0,
          lastResetAt: getTodayStartTimestamp(),
        });

        return true;
      }
    } else {
      // Nếu chưa có dailyUsage → Khởi tạo
      console.log(`🆕 Initializing daily usage for user ${userId}`);

      await set(dailyUsageRef, {
        currentDate: currentDate,
        usedCount: 0,
        lastResetAt: getTodayStartTimestamp(),
        customLimit: null,
      });

      return true;
    }

    return false; // Không cần reset
  } catch (error) {
    console.error("❌ Error resetting daily usage:", error);
    return false;
  }
};

/**
 * Kiểm tra xem user còn lượt sử dụng không
 * @param {string} userId
 * @returns {Promise<Object>} { success: boolean, remaining: number, limit: number, reason?: string }
 */
export const checkDailyLimit = async (userId) => {
  try {
    if (!userId) {
      return {
        success: false,
        remaining: 0,
        limit: 0,
        reason: "no_user",
        message: "Vui lòng đăng nhập để sử dụng tính năng này",
      };
    }

    const db = getDatabase();

    // 1. Reset nếu cần (qua ngày mới)
    await resetDailyUsageIfNeeded(userId);

    // 2. Lấy daily limit cho user
    const dailyLimit = await getDailyLimit(userId);

    // 3. Lấy số lượt đã sử dụng
    const dailyUsageRef = ref(db, `/users/${userId}/dailyUsage`);
    const dailyUsageSnapshot = await get(dailyUsageRef);

    let usedCount = 0;

    if (dailyUsageSnapshot.exists()) {
      const dailyUsageData = dailyUsageSnapshot.val();
      usedCount = dailyUsageData.usedCount || 0;
    }

    const remaining = Math.max(0, dailyLimit - usedCount);

    // 4. Check xem còn lượt không
    if (usedCount >= dailyLimit) {
      const timeUntilReset = getTimeUntilReset();
      return {
        success: false,
        remaining: 0,
        limit: dailyLimit,
        used: usedCount,
        reason: "limit_reached",
        message: `Bạn đã hết lượt sử dụng hôm nay (${usedCount}/${dailyLimit})`,
        resetTime: timeUntilReset,
      };
    }

    // 5. Còn lượt
    return {
      success: true,
      remaining: remaining,
      limit: dailyLimit,
      used: usedCount,
      message: `Bạn còn ${remaining}/${dailyLimit} lượt sử dụng hôm nay`,
    };
  } catch (error) {
    console.error("❌ Error checking daily limit:", error);
    return {
      success: false,
      remaining: 0,
      limit: 0,
      reason: "error",
      message: "Lỗi khi kiểm tra giới hạn sử dụng. Vui lòng thử lại",
      error: error.message,
    };
  }
};

/**
 * Trừ 1 lượt sử dụng cho user (với Transaction để tránh race condition)
 * @param {string} userId
 * @param {string} requestId - ID của request (để tracking)
 * @param {string} type - Loại request (price_analysis, rental_analysis, etc.)
 * @returns {Promise<Object>} { success: boolean, remaining: number }
 */
export const deductDailyUsage = async (
  userId,
  requestId,
  type = "price_analysis"
) => {
  try {
    if (!userId) {
      return {
        success: false,
        remaining: 0,
        message: "User ID không hợp lệ",
      };
    }

    const db = getDatabase();
    const dailyUsageRef = ref(db, `/users/${userId}/dailyUsage`);

    // Sử dụng Transaction để đảm bảo atomic operation
    const result = await runTransaction(dailyUsageRef, (currentData) => {
      if (currentData === null) {
        // Khởi tạo nếu chưa có data
        return {
          currentDate: getCurrentDateVN(),
          usedCount: 1,
          lastResetAt: getTodayStartTimestamp(),
          customLimit: null,
        };
      }

      // Increment usedCount
      currentData.usedCount = (currentData.usedCount || 0) + 1;
      return currentData;
    });

    if (result.committed) {
      const newUsedCount = result.snapshot.val().usedCount;
      const dailyLimit = await getDailyLimit(userId);
      const remaining = Math.max(0, dailyLimit - newUsedCount);

      // Lưu vào usage history
      await saveUsageHistory(userId, requestId, type);

      console.log(
        `✅ Daily usage deducted for user ${userId}: ${newUsedCount}/${dailyLimit}`
      );

      return {
        success: true,
        remaining: remaining,
        used: newUsedCount,
        limit: dailyLimit,
        message: `Đã trừ lượt sử dụng. Còn ${remaining}/${dailyLimit} lượt`,
      };
    } else {
      throw new Error("Transaction not committed");
    }
  } catch (error) {
    console.error("❌ Error deducting daily usage:", error);
    return {
      success: false,
      remaining: 0,
      message: "Lỗi khi trừ lượt sử dụng",
      error: error.message,
    };
  }
};

/**
 * Lưu lịch sử sử dụng cho analytics
 * @param {string} userId
 * @param {string} requestId
 * @param {string} type
 */
const saveUsageHistory = async (userId, requestId, type) => {
  try {
    const db = getDatabase();
    const currentDate = getCurrentDateVN();
    const timestamp = Date.now();

    // 1. Lưu vào usage history của user
    const historyRef = ref(db, `/users/${userId}/usageHistory/${currentDate}`);
    const historySnapshot = await get(historyRef);

    let historyData = {
      count: 1,
      timestamps: [timestamp],
      details: [
        {
          requestId,
          type,
          timestamp,
        },
      ],
    };

    if (historySnapshot.exists()) {
      const existingData = historySnapshot.val();
      historyData = {
        count: (existingData.count || 0) + 1,
        timestamps: [...(existingData.timestamps || []), timestamp],
        details: [
          ...(existingData.details || []),
          {
            requestId,
            type,
            timestamp,
          },
        ],
      };
    }

    await set(historyRef, historyData);

    // 2. Update analytics data (cho admin dashboard)
    const analyticsRef = ref(db, `/analytics/daily/${currentDate}`);
    await runTransaction(analyticsRef, (currentData) => {
      if (currentData === null) {
        return {
          totalRequests: 1,
          uniqueUsers: 1,
          userIds: [userId],
          lastUpdated: timestamp,
        };
      }

      currentData.totalRequests = (currentData.totalRequests || 0) + 1;

      // Track unique users
      const userIds = currentData.userIds || [];
      if (!userIds.includes(userId)) {
        userIds.push(userId);
        currentData.uniqueUsers = userIds.length;
        currentData.userIds = userIds;
      }

      currentData.lastUpdated = timestamp;
      return currentData;
    });
  } catch (error) {
    console.error("❌ Error saving usage history:", error);
    // Không throw error vì đây là secondary operation
  }
};

/**
 * Lấy số lượt còn lại của user
 * @param {string} userId
 * @returns {Promise<number>}
 */
export const getRemainingUsage = async (userId) => {
  try {
    const result = await checkDailyLimit(userId);
    return result.remaining || 0;
  } catch (error) {
    console.error("❌ Error getting remaining usage:", error);
    return 0;
  }
};

/**
 * Admin function: Set custom daily limit cho user cụ thể
 * @param {string} userId
 * @param {number} customLimit
 * @param {string} reason
 * @param {string} adminUserId
 * @param {number|null} expiresAt - Timestamp khi custom limit hết hạn (null = permanent)
 * @returns {Promise<Object>}
 */
export const setCustomDailyLimit = async (
  userId,
  customLimit,
  reason,
  adminUserId,
  expiresAt = null
) => {
  try {
    const db = getDatabase();
    const customLimitRef = ref(db, `/custom-limits/${userId}`);

    const customLimitData = {
      dailyLimit: customLimit,
      reason: reason || "Admin override",
      setBy: adminUserId,
      setAt: Date.now(),
      expiresAt: expiresAt,
    };

    await set(customLimitRef, customLimitData);

    console.log(
      `✅ Set custom limit for user ${userId}: ${customLimit} lượt/ngày`
    );

    return {
      success: true,
      message: `Đã set giới hạn ${customLimit} lượt/ngày cho user`,
    };
  } catch (error) {
    console.error("❌ Error setting custom daily limit:", error);
    return {
      success: false,
      message: "Lỗi khi set giới hạn custom",
      error: error.message,
    };
  }
};

/**
 * Admin function: Remove custom limit cho user (về default)
 * @param {string} userId
 * @returns {Promise<Object>}
 */
export const removeCustomDailyLimit = async (userId) => {
  try {
    const db = getDatabase();
    const customLimitRef = ref(db, `/custom-limits/${userId}`);

    await set(customLimitRef, null);

    console.log(`✅ Removed custom limit for user ${userId}`);

    return {
      success: true,
      message: "Đã xóa giới hạn custom, user sẽ dùng default limit",
    };
  } catch (error) {
    console.error("❌ Error removing custom daily limit:", error);
    return {
      success: false,
      message: "Lỗi khi xóa giới hạn custom",
      error: error.message,
    };
  }
};

/**
 * Admin function: Update default daily limit cho toàn hệ thống
 * @param {number} newLimit
 * @param {string} adminUserId
 * @returns {Promise<Object>}
 */
export const updateDefaultDailyLimit = async (newLimit, adminUserId) => {
  try {
    const db = getDatabase();
    const defaultLimitRef = ref(db, `/system-settings/dailyLimits`);

    const settingsData = {
      defaultLimit: newLimit,
      lastUpdatedAt: Date.now(),
      lastUpdatedBy: adminUserId,
    };

    await set(defaultLimitRef, settingsData);

    console.log(`✅ Updated default daily limit to ${newLimit} lượt/ngày`);

    return {
      success: true,
      message: `Đã cập nhật giới hạn mặc định: ${newLimit} lượt/ngày`,
    };
  } catch (error) {
    console.error("❌ Error updating default daily limit:", error);
    return {
      success: false,
      message: "Lỗi khi cập nhật giới hạn mặc định",
      error: error.message,
    };
  }
};
